import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Check, ArrowRight } from 'lucide-react';
import { products } from '../data/mock';

const Products = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Home', 'Commercial', 'Retrofit'];

  const filteredProducts = selectedCategory === 'All' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  const handleRequestQuote = (productName) => {
    const phone = "+919217401431";
    const message = `Hi, I'm interested in ${productName}. Please share more details.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Our Smart RO Products</h1>
          <p className="text-xl text-cyan-50 max-w-3xl mx-auto">
            Advanced IoT-enabled water purification systems designed for every need - from homes to large commercial spaces
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-12">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-4 h-12">
              {categories.map((category) => (
                <TabsTrigger key={category} value={category} className="text-base">
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Products Grid */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {filteredProducts.map((product, index) => (
              <Card 
                key={product.id}
                className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                  <Badge className="absolute top-4 right-4 bg-gradient-to-r from-cyan-500 to-teal-500 text-white border-0">
                    {product.category}
                  </Badge>
                </div>

                <CardHeader>
                  <CardTitle className="text-2xl">{product.name}</CardTitle>
                  <CardDescription className="text-base">
                    {product.idealFor}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-grow space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Capacity:</span>
                      <span className="text-sm font-semibold text-gray-900">{product.capacity}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Purification:</span>
                      <span className="text-sm font-semibold text-gray-900">{product.stages.split('(')[0]}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="font-semibold mb-3 text-gray-900">Key Features:</h4>
                    <ul className="space-y-2">
                      {product.features.slice(0, 5).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>

                <CardFooter className="flex flex-col gap-3 pt-6 border-t">
                  <div className="flex items-baseline justify-between w-full">
                    <span className="text-3xl font-bold text-gray-900">{product.price}</span>
                    <span className="text-sm text-gray-500">+ installation</span>
                  </div>
                  <Button 
                    onClick={() => handleRequestQuote(product.name)}
                    className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                  >
                    Request Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Comparison Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Product Comparison
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-900">Feature</th>
                    {products.map((product) => (
                      <th key={product.id} className="text-center py-4 px-4 font-semibold text-gray-900">
                        {product.name.replace('Niloman HX ', '')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-4 font-medium text-gray-700">Capacity</td>
                    {products.map((product) => (
                      <td key={product.id} className="py-4 px-4 text-center text-gray-600">
                        {product.capacity}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <td className="py-4 px-4 font-medium text-gray-700">Purification Stages</td>
                    {products.map((product) => (
                      <td key={product.id} className="py-4 px-4 text-center text-gray-600">
                        {product.stages.split('(')[0]}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-4 font-medium text-gray-700">Connectivity</td>
                    <td className="py-4 px-4 text-center text-gray-600">Wi-Fi</td>
                    <td className="py-4 px-4 text-center text-gray-600">Wi-Fi + 4G</td>
                    <td className="py-4 px-4 text-center text-gray-600">Wi-Fi</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <td className="py-4 px-4 font-medium text-gray-700">Mobile App</td>
                    {products.map((product) => (
                      <td key={product.id} className="py-4 px-4 text-center">
                        <Check className="h-5 w-5 text-teal-600 mx-auto" />
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-4 font-medium text-gray-700">API Integration</td>
                    <td className="py-4 px-4 text-center text-gray-400">-</td>
                    <td className="py-4 px-4 text-center">
                      <Check className="h-5 w-5 text-teal-600 mx-auto" />
                    </td>
                    <td className="py-4 px-4 text-center text-gray-400">-</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <td className="py-4 px-4 font-medium text-gray-700">Best For</td>
                    {products.map((product) => (
                      <td key={product.id} className="py-4 px-4 text-center text-sm text-gray-600">
                        {product.idealFor}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-4 font-medium text-gray-700">Price</td>
                    {products.map((product) => (
                      <td key={product.id} className="py-4 px-4 text-center font-bold text-cyan-600">
                        {product.price}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Need Help Choosing the Right Product?
          </h2>
          <p className="text-lg mb-8 text-cyan-50">
            Our experts are here to help you select the perfect smart RO system for your needs
          </p>
          <Button 
            onClick={() => window.location.href = '/contact'}
            size="lg" 
            className="bg-white text-cyan-600 hover:bg-gray-100"
          >
            Talk to an Expert
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Products;
